/* <skin-quiz> — port of the OLD skin-quiz inline Alpine component. Listens
   for Quiz Kit app events (the quiz itself is the app embed in page.content)
   and builds recommendation cards from the Section Rendering API endpoints
   skin-quiz-product + quiz-product-description. Card markup lives in the
   section's <template> islands; ATC goes through cart.js (<product-form>
   wraps a real /cart/add form — this module only maintains the hidden
   id/properties inputs; the Quiz Kit add-to-cart report rides on cart.js's
   product-form:added event, #268). While a Buy 1 Get 1 shows on a recommended
   product, its Add to cart opens the product page's free-gift chooser first,
   and the chooser's settled add sends that same event (renderCards, #656).
   Null-guarded throughout. */

const CONCERNS_QID = 124682, BREAKOUTS_QID = 114362, QUIZ_ID = '27061';
const q = (r, s) => r.querySelector(s);
const qa = (r, s) => r.querySelectorAll(s);
const clone = (root, name) => q(root, `[data-tpl-${name}]`)?.content.firstElementChild.cloneNode(true);
/* #283 (2026-10-17): availability the way Shopify judges it. Quiz Kit's payload is Admin-REST
   shaped (no `available`), so a variant set to continue selling, or with untracked inventory,
   is available at any stock count; only a tracked, deny-policy variant at 0 or below is sold
   out. The old `inventory_quantity <= 0` showed the two backorder Solabiome products SOLD OUT. */
const soldOut = (v) => {
  if (!v) return true;
  if (typeof v.available === 'boolean') return !v.available;
  if (String(v.inventory_policy).toLowerCase() === 'continue') return false;
  if ('inventory_management' in v && !v.inventory_management) return false;
  return v.inventory_quantity <= 0;
};

class SkinQuiz extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.answered = false;
    this.parent = this.closest('.skin-quiz-custom-section');
    try {
      this.claims = JSON.parse(q(this, '[data-product-claims]')?.textContent || '{}').blocks || {};
    } catch (e) {
      this.claims = {};
    }
    document.addEventListener('quizKitQuestionLoaded', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.answered = true;
    });
    document.addEventListener('quizKitResultsPageLoaded', (e) => {
      if (e.detail) this.showResults(e.detail);
    });
  }

  helpsReduce(detail, index) {
    const mk = detail.result?.marketingFields || {};
    if (mk.subtitle) {
      const groups = mk.subtitle.split('|').map((g) => g.trim());
      return groups[index] ? groups[index].split(',').map((c) => c.trim()) : [];
    }
    const ans = detail.answers || [];
    const concerns = ans.find((x) => x.questionId == CONCERNS_QID);
    const breakouts = ans.find((x) => x.questionId == BREAKOUTS_QID);
    if (!concerns || !breakouts) return [];
    const top3 = concerns.selectedAnswers.map((o) => o.value).slice(0, 3);
    const b = breakouts.selectedAnswers[0]?.value;
    if (index === 0 || b === 'No' || b === 'Full Face Breakouts') return top3;
    if (b === 'Occasional Pimples')
      return concerns.selectedAnswers.some((x) => x.value === 'Redness')
        ? ['Occasional Pimples', 'Redness']
        : ['Occasional Pimples'];
    return [];
  }

  /* #656 (2026-10-23): the same per-product fetch now also brings the price row's Buy 1 Get 1 tag text and,
     while that offer shows on the product, the product page's free-gift chooser (sections/quiz-product-description).
     Any failure keeps the fallback description, no tag and no chooser: a plain add, as before. */
  async extras(handle, fallback) {
    const out = { html: fallback, chip: '', modal: null };
    try {
      const res = await fetch(`/products/${handle}?section_id=quiz-product-description`);
      if (!res.ok) return out;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const el = q(doc, '[data-quiz-product-description]');
      if (el && el.innerHTML.trim()) out.html = el.innerHTML.trim();
      out.chip = q(doc, 'template[data-quiz-price-chip]')?.content.textContent.trim() || '';
      out.modal = q(doc, 'bogo-gift-modal');
    } catch (e) {
      /* the fallback description stands */
    }
    return out;
  }

  buildVariants(card, info, chip) {
    for (const opt of info.options || []) {
      const choose = q(card, '.recommended-variant-selection-text span');
      if (choose) choose.textContent = opt.name;
      const inStock = opt.values.filter((v) => info.variants.some((x) => x.title == v)).length;
      /* #283: the first AVAILABLE value starts selected (its price, image and id), else the first. */
      const first = Math.max(
        0,
        opt.values.findIndex((v) => !soldOut(info.variants.find((x) => x.title == v)))
      );
      card.dataset.quizFirst = first;

      opt.values.forEach((value, i) => {
        const variant = info.variants.find((x) => x.title == value);
        if (!variant) return;
        const oos = soldOut(variant);

        const node = clone(card, 'swatch');
        if (!node) return;
        if (inStock < 2) node.classList.add('hidden');
        const label = q(node, 'label');
        label.setAttribute('tabindex', '0');
        label.dataset.index = i; /* #161 F6: a positive tabindex hijacked the page's tab order */
        const input = q(node, 'input');
        input.id = variant.id;
        input.name = 'option-' + opt.name.toLowerCase();
        input.value = value;
        /* #268 (2026-09-09, Codex F16): each radio carries its own variant's price so the vendor
           report can read the SELECTED variant instead of the first price node in the card. Same
           raw Quiz Kit value the visible .product-current-price gets below (a dollar amount,
           parseFloat'd at report time) — one normalisation path, not two. */
        if (variant.price != null) input.dataset.price = variant.price;
        if (i === first) input.checked = true;
        if (opt.name === 'Color') {
          q(node, '[data-swatch-text]')?.remove();
          const sw = value.replace(/\s+/g, '-').toLowerCase();
          const dot = q(node, '[data-swatch-dot]');
          dot.classList.add('sol-swatch-' + sw);
          dot.setAttribute('aria-label', sw);
          if (oos) dot.classList.add('slashed-selector', 'peer-checked:slashed-selector-active');
        } else {
          q(node, '[data-swatch-color]')?.remove();
          q(node, '[data-swatch-text]').textContent = value.toLowerCase();
        }
        if (i === first) {
          if (oos) q(card, '.variant-availability')?.setAttribute('data-sold-out', 'true');
          const sel = q(card, '.selected-variant-text');
          if (sel) sel.textContent = value;
          q(card, '[data-quiz-variant-id]')?.setAttribute('value', variant.id);
        }
        q(card, '[data-variant-swatches]')?.appendChild(node);

        const price = clone(card, 'price');
        if (!price) return;
        if (i !== first) price.classList.add('hidden');
        q(price, '.product-current-price').textContent = variant.price;
        /* #656 (Eli 2026-10-23, "dont show the 499 strikethrough, just 399"): no struck price, no "SAVE x%".
           The product page's one tag joins the price while a Buy 1 Get 1 shows on the product; a sold-out
           variant shows the price alone, as its product page drops the tag for one. */
        const tag = q(price, '[data-price-chip]');
        if (tag && chip && !oos) tag.textContent = chip;
        else tag?.remove();
        q(card, '.recommended-variant-pricing')?.appendChild(price);
      });

      if (opt.values.length <= 1 && opt.name !== 'Color') {
        const holder = q(card, '.recommended-variant-holder');
        if (holder) holder.style.display = 'none';
      }
    }
  }

  buildMedia(card, info) {
    const wrap = q(card, '.recommended-image');
    if (wrap) {
      const imgs = (info.variants || []).map((v) => ({ id: v.id, src: v.image && v.image.src }));
      const list = imgs.length > 1 ? imgs : [{ src: info.image?.src }];
      const shown = list.length > 1 ? Number(card.dataset.quizFirst || 0) : 0; /* #283 */
      list.forEach((item, i) => {
        if (!item.src) return;
        const img = document.createElement('img');
        img.src = item.src;
        img.alt = info.title || '';
        img.loading = 'lazy';
        if (item.id) img.dataset.id = item.id;
        if (i !== shown) img.classList.add('hidden');
        wrap.appendChild(img);
      });
      wrap.setAttribute('href', '/products/' + info.handle);
    }
    qa(card, '.recommended-title').forEach((el) => (el.textContent = info.title));
    qa(card, '.recommended-reviews').forEach((el) => {
      const badge = clone(card, 'review');
      if (!badge) return;
      badge.href = '/products/' + info.handle;
      const span = q(badge, 'span');
      span.dataset.id = info.id;
      span.dataset.productSku = info.handle;
      span.dataset.productTitle = info.title;
      span.dataset.productType = info.product_type;
      el.appendChild(badge);
    });
  }

  wireCard(card, quizAnswers) {
    qa(card, '[data-variant-swatches] label').forEach((label) => {
      label.addEventListener('click', () => {
        const i = label.dataset.index;
        const input = q(label, 'input');
        const product = label.closest('.recommended-product');
        if (!input || !product) return;
        const sel = q(product, '.selected-variant-text');
        if (sel) sel.textContent = input.value;
        q(product, '[data-quiz-variant-id]')?.setAttribute('value', input.id);
        q(product, '.variant-availability')?.setAttribute(
          'data-sold-out',
          q(label, '.slashed-selector') ? 'true' : 'false'
        );
        qa(product, '.recommended-image img').forEach((img, j) => img.classList.toggle('hidden', String(j) !== i));
        qa(product, '.recommended-variant-pricing-index').forEach((p, j) =>
          p.classList.toggle('hidden', String(j) !== i)
        );
      });
    });

    const form = q(card, 'product-form form');
    if (!form) return;
    for (const [name, value] of [['_quiz_kit_id', QUIZ_ID], ['_quiz_kit_answers', quizAnswers]]) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      form.appendChild(input);
    }
    /* #268 (2026-09-09, Codex F16): this report used to fire on the form's submit event — before
       the theme's add had succeeded, so a refused add (sold-out race, 422, network error) was still
       reported to Quiz Kit as an add-to-cart. cart.js dispatches `product-form:added` on the form
       only after CartAPI.add resolves (detail: { item, response }); a failed add never dispatches
       it, so a failed add is never reported. Price: the checked radio's own variant price (set in
       buildVariants), else the visible pricing row, else the first price node as before. */
    form.addEventListener('product-form:added', () => {
      const id = parseInt(q(form, '[data-quiz-variant-id]')?.value, 10);
      if (!id) return;
      const price =
        parseFloat(q(form, 'input[type=radio]:checked')?.dataset.price) ||
        parseFloat(q(card, '.recommended-variant-pricing-index:not(.hidden) .product-current-price')?.textContent) ||
        parseFloat(q(card, '.product-current-price')?.textContent) ||
        0;
      fetch('https://api.quizkitapp.com/api/v1/quizzes/z6SbEN2F0/add-to-cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price,
          variantId: id,
          source: 'quiz_result',
          properties: { _quiz_kit_id: QUIZ_ID, _quiz_kit_answers: quizAnswers },
        }),
      }).catch(() => {});
    });
  }

  /* #283 (2026-10-17, Codex F13): the cards are built out of sight and swapped in whole, so a
     half-built card never shows; if the section fetch fails, a Try again button takes their
     place and re-runs this. Description fetches keep their own fallback (description()). */
  async renderCards(holder, detail, headings, quizAnswers) {
    try {
      const res = await fetch('/?sections=skin-quiz-product');
      if (!res.ok) throw new Error('skin-quiz-product ' + res.status);
      const data = await res.json();
      if (!data || !data['skin-quiz-product']) throw new Error('skin-quiz-product empty');
      const cards = [];
      const choosers = [];
      for (const [index] of headings.entries()) {
        const keys = Object.keys(detail.result.productIds || {});
        const info = (detail.recommendedProducts || []).find((x) => x.id == keys[index]);
        if (!info) continue;
        const doc = new DOMParser().parseFromString(data['skin-quiz-product'], 'text/html');
        const card = document.createElement('div');
        card.append(...doc.body.children);

        const num = q(card, '.top-recommendation span');
        if (num) num.textContent = index + 1;

        const pillHolder = q(card, '.recommended-helps-reduces');
        if (pillHolder)
          for (const label of this.helpsReduce(detail, index)) {
            const pill = clone(card, 'pill');
            if (!pill) break;
            const iconWrap = q(pill, '[data-pill-icon]');
            if (this.claims[label]) {
              const img = document.createElement('img');
              img.src = this.claims[label];
              img.alt = '';
              img.width = img.height = 20;
              iconWrap.appendChild(img);
            } else {
              iconWrap.remove();
            }
            q(pill, '[data-pill-label]').textContent = label;
            pillHolder.appendChild(pill);
          }

        const extra = await this.extras(info.handle, info.body_html);
        const desc = q(card, '.product-description');
        if (desc) desc.innerHTML = extra.html;

        this.buildVariants(card, info, extra.chip);
        this.buildMedia(card, info);
        this.wireCard(card, quizAnswers);
        cards.push(card);
        if (extra.modal) choosers.push([card, extra.modal, index]);
      }
      holder.replaceChildren(...cards);
      /* #656: a chooser finds its form by id when it connects, so it goes in only after the cards are on the page.
         Each card's form gets its own id, and each chooser its own title id (two cards = two choosers). */
      for (const [card, modal, index] of choosers) {
        const form = q(card, 'product-form form');
        if (!form) continue;
        /* setAttribute, never form.id: the form's own <input name="id"> shadows that property (it read back as
           the input, and the chooser looked for a form called "[object HTMLInputElement]" — the staging check) */
        const formId = `quiz-product-form-${index}`;
        form.setAttribute('id', formId);
        modal.dataset.formId = formId;
        const title = q(modal, '[id^="bogo-gift-title-"]');
        if (title) {
          title.id = `bogo-gift-title-quiz-${index}`;
          q(modal, 'dialog')?.setAttribute('aria-labelledby', title.id);
        }
        /* The chooser takes the form's submit, so cart.js never sends product-form:added for that add. Its
           receipt row turns "done" only once the cart has answered for the paid item (#368), so that turn
           sends the same event, and the Quiz Kit report above still hears only settled adds (#268). */
        const receipt = q(modal, '[data-bogo-receipt]');
        if (receipt) {
          new MutationObserver(() => {
            if (receipt.dataset.state === 'done') form.dispatchEvent(new CustomEvent('product-form:added', { bubbles: true }));
          }).observe(receipt, { attributes: true, attributeFilter: ['data-state'] });
        }
        card.appendChild(modal);
      }
    } catch (e) {
      const wrap = document.createElement('div');
      wrap.className = 'flex justify-center py-6';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'btn btn-primary-outline';
      retry.textContent = 'Try again';
      retry.addEventListener(
        'click',
        () => {
          retry.disabled = true;
          this.renderCards(holder, detail, headings, quizAnswers);
        },
        { once: true }
      );
      wrap.appendChild(retry);
      holder.replaceChildren(wrap);
    }
  }

  async showResults(detail) {
    this.parent?.classList.add('visible-results');
    const quizAnswers = (detail.answers || [])
      .map((a) => `${a.questionTitle}: ${a.selectedAnswers.map((x) => x.value).join(', ')}`)
      .join('; ')
      .replace(/<br>\n/g, '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    q(this.parent || this, '[data-quiz-kit-key]')?.classList.add('hidden-results');
    this.classList.remove('hidden-results');

    const holder = q(this, '.recommendation-holder');
    if (!holder) return;
    holder.innerHTML = '';

    const cb = detail.result?.contentBlocks || {};
    const headings = Object.keys(cb)
      .filter((k) => k.startsWith('headings'))
      .map((k) => cb[k])
      .filter((h) => h && String(h.title || '').indexOf('Your results are in') === -1);

    if (headings.length) await this.renderCards(holder, detail, headings, quizAnswers);

    /* #656 (2026-10-23): the discount box that used to be filled here (Quiz Kit's discount-code field, a copy
       button) is gone; the Klaviyo results event below is unchanged. */
    if (detail.userData?.email && this.answered) {
      const orders = detail.result?.productOrders || [];
      const prods = detail.recommendedProducts || [];
      const p1 = prods.find((x) => x.id == orders[0]);
      const p2 = orders.length >= 2 ? prods.find((x) => x.id == orders[1]) : null;
      this.track(detail.userData.email, {
        'Quiz Kit Results Link': location.href,
        'Quiz Results Name': detail.result?.name,
        'Quiz Kit Product 1 Name': p1?.title || '',
        'Quiz Kit Product 1 Description': headings[0]?.description || '',
        'Quiz Kit Product 2 Name': p2?.title || '',
        'Quiz Kit Product 2 Description': headings[1]?.description || '',
      });
    }

    q(this, '.retake-quiz')?.addEventListener(
      'click',
      () => {
        q(this.parent || this, '#layout-1-results-retake-button')?.click();
        q(this.parent || this, '[data-quiz-kit-key]')?.classList.remove('hidden-results');
        this.classList.add('hidden-results');
        this.parent?.classList.remove('visible-results');
      },
      { once: true }
    );
  }

  track(email, items, tries = 0) {
    const k = window._learnq;
    if (!k || !k.identify || !k.track) {
      if (tries < 40) setTimeout(() => this.track(email, items, tries + 1), 250);
      return;
    }
    k.identify(Object.assign({ email }, items), function () {});
    k.push(['track', 'Presidio - Quiz: Results Rec', items]);
  }
}

if (!customElements.get('skin-quiz')) customElements.define('skin-quiz', SkinQuiz);
